import React, { useEffect, useState } from 'react';
import { accent, darkPurple } from '../utils/colors';
import { fetchSkillGradeTable, createSkillToGradeBinding, deleteSkillToGradeBinding, updateSkillToGrade } from '../utils/api';
import useSortedChildSkills from '../hooks/useSortedChildSkills';
import SkillRow from './SkillRow';

const SkillsMatrix = ({ apiBase }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skillGroups, setSkillGroups] = useState([]);
  const [grades, setGrades] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [selectedGradeId, setSelectedGradeId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchSkillGradeTable(apiBase);
        if (!cancelled && data) {
          setSkillGroups(data.skillGroups || []);

          const apiGrades = (data.grades || [])
            .map(g => ({ id: String(g.id), label: g.title, orderNum: g.orderNum }))
            .sort((a, b) => (a.orderNum - b.orderNum) || a.label.localeCompare(b.label, 'ru'));
          setGrades(apiGrades);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Ошибка загрузки матрицы навыков');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (apiBase) run();
    return () => { cancelled = true; };
  }, [apiBase]);


  const sortedChildSkillsByParent = useSortedChildSkills(skillGroups, grades, selectedGradeId);

  const onChangeSkillLevel = async (skillId, gradeId, nextLevelRaw) => {
    const hasValue = nextLevelRaw !== '' && nextLevelRaw != null;
    const nextLevel = hasValue ? Number(nextLevelRaw) : null;
    const prevSkillGroups = JSON.parse(JSON.stringify(skillGroups));

    setSkillGroups(current => current.map(group => ({
      ...group,
      childSkills: group.childSkills.map(skill => {
        if (skill.skillId !== skillId) return skill;
        return {
          ...skill,
          gradeFlags: skill.gradeFlags.map(flag => {
            if (flag.gradeId !== gradeId) return flag;
            const isChecked = nextLevel != null;
            return { ...flag, isChecked, skillLevel: isChecked ? nextLevel : undefined };
          })
        };
      })
    })));

    try {
      if (nextLevel == null) {
        await deleteSkillToGradeBinding(apiBase, skillId, gradeId);
      } else {
        const currentFlag = (skillGroups.find(g => g.childSkills.some(s => s.skillId === skillId))
          ?.childSkills.find(s => s.skillId === skillId)
          ?.gradeFlags || []).find(f => f.gradeId === gradeId);

        if (currentFlag && currentFlag.id != null) {
          await updateSkillToGrade(apiBase, { id: currentFlag.id, skillId, gradeId, skillLevel: nextLevel });
        } else {
          const resp = await createSkillToGradeBinding(apiBase, skillId, gradeId, nextLevel);
          const maybeId = resp?.payload ?? resp;
          if (typeof maybeId === 'number') {
            setSkillGroups(current => current.map(group => ({
              ...group,
              childSkills: group.childSkills.map(skill => {
                if (skill.skillId !== skillId) return skill;
                return {
                  ...skill,
                  gradeFlags: skill.gradeFlags.map(flag => flag.gradeId === gradeId ? { ...flag, id: maybeId } : flag)
                };
              })
            })));
          }
        }
      }
    } catch (e) {
      setError(e.message || 'Ошибка сохранения уровня навыка');
      setSkillGroups(prevSkillGroups);
    }
  };

  function onToggleParent(parentId) {
    setExpanded(prev => ({
      ...prev,
      [parentId]: !prev[parentId]
    }));
  }

  function onToggleGradeHeader(gradeId) {
    setSelectedGradeId(prev => (prev === gradeId ? null : gradeId));
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 4px 24px #a259f733' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ color: accent, fontWeight: 700, fontSize: 20 }}>Матрица навыков по грейдам</div>
      </div>

      {loading && <div style={{ color: darkPurple }}>Загрузка...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {!loading && !error && (
        <div style={{ overflow: 'auto', maxHeight: '80vh', minHeight: '60vh', border: '1px solid #eee', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: '#faf7ff', color: darkPurple, padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #f0e9ff' }}>Навык</th>
                {grades.map(g => (
                  <th
                    key={g.id}
                    onClick={() => onToggleGradeHeader(g.id)}
                    style={{
                      background: String(selectedGradeId) === String(g.id) ? '#e9dcff' : '#faf7ff',
                      color: darkPurple,
                      padding: '10px 12px',
                      borderBottom: '1px solid #f0e9ff',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    aria-selected={String(selectedGradeId) === String(g.id)}
                  >
                    {g.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {skillGroups.map(group => {
                const parent = group.parentSkill;
                const isOpen = !!expanded[parent.id];
                return (
                  <React.Fragment key={`group-${parent.id}`}>
                    <tr onClick={() => onToggleParent(parent.id)} style={{ cursor: 'pointer' }}>
                      <td colSpan={1 + grades.length} style={{ background: '#faf7ff', padding: '12px 16px', borderBottom: '1px solid #f0e9ff', fontWeight: 800, textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', width: 16 }}>{isOpen ? '▾' : '▸'}</span>
                        <span style={{ marginLeft: 8 }}>{parent.title}</span>
                      </td>
                    </tr>
                    {isOpen && (
                      (() => {
                        const childSkillsList = sortedChildSkillsByParent[parent.id] || group.childSkills;
                        const filteredSkills = childSkillsList.filter(skill => skill.skillId !== parent.id);
                        return filteredSkills.length === 0 ? (
                          <tr><td colSpan={1 + grades.length} style={{ padding: 10, color: '#888' }}>Нет дочерних навыков</td></tr>
                        ) : (
                          filteredSkills.map(skill => (
                            <SkillRow key={skill.skillId} skill={skill} grades={grades} onChangeSkillLevel={onChangeSkillLevel} />
                          ))
                        );
                      })()
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SkillsMatrix;